import { getSoundMutedPreference } from '@/lib/notifications/soundPreferences';

export type NotificationSoundKind =
  | 'guild-chat'
  | 'mass-published'
  | 'mass-starting-soon'
  | 'mass-signup';

interface ToneStep {
  frequency: number;
  durationMs: number;
  volume: number;
  type: OscillatorType;
}

const soundPatterns: Record<NotificationSoundKind, ToneStep[]> = {
  'guild-chat': [
    { frequency: 660, durationMs: 120, volume: 0.08, type: 'sine' },
  ],
  'mass-published': [
    { frequency: 880, durationMs: 110, volume: 0.12, type: 'triangle' },
    { frequency: 1040, durationMs: 140, volume: 0.1, type: 'triangle' },
  ],
  'mass-starting-soon': [
    { frequency: 540, durationMs: 100, volume: 0.1, type: 'sawtooth' },
    { frequency: 760, durationMs: 100, volume: 0.1, type: 'sawtooth' },
    { frequency: 980, durationMs: 120, volume: 0.12, type: 'triangle' },
  ],
  'mass-signup': [
    { frequency: 520, durationMs: 90, volume: 0.09, type: 'square' },
    { frequency: 720, durationMs: 120, volume: 0.08, type: 'square' },
  ],
};

function getAudioContextConstructor():
  | typeof AudioContext
  | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
}

export async function playNotificationSound(kind: NotificationSoundKind): Promise<void> {
  if (getSoundMutedPreference()) {
    return;
  }

  const AudioContextConstructor = getAudioContextConstructor();

  if (!AudioContextConstructor) {
    return;
  }

  const audioContext = new AudioContextConstructor();
  const pattern = soundPatterns[kind];

  try {
    let cursor = audioContext.currentTime;

    for (const step of pattern) {
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const durationSeconds = step.durationMs / 1000;

      oscillator.type = step.type;
      oscillator.frequency.setValueAtTime(step.frequency, cursor);
      gain.gain.setValueAtTime(0.0001, cursor);
      gain.gain.exponentialRampToValueAtTime(step.volume, cursor + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, cursor + durationSeconds);

      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start(cursor);
      oscillator.stop(cursor + durationSeconds + 0.02);

      cursor += durationSeconds + 0.06;
    }

    const totalDurationMs = pattern.reduce((sum, step) => sum + step.durationMs, 0) + pattern.length * 60 + 80;
    await new Promise((resolve) => window.setTimeout(resolve, totalDurationMs));
  } catch {
    // Browser autoplay restrictions are expected sometimes.
  } finally {
    await audioContext.close().catch(() => {
      // Ignore cleanup failures.
    });
  }
}
